/* eslint-disable no-underscore-dangle */
import {
  AutocompleteOptions,
  AutocompleteState,
  createAutocomplete,
} from '@algolia/autocomplete-core';
import { Hit } from '@algolia/client-search';
import React, {
  BaseSyntheticEvent,
  JSXElementConstructor,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import useWindowDimensions from '../../../hooks/useWindowDimensions';
import useOutsideClickAlerter from '../../../hooks/useOutsideClickAlerter';

type AutocompleteItem = Hit<{
  objectID: string,
  id: number,
  __autocomplete_id: number,
  route?: string,
}>;

interface Props extends Partial<AutocompleteOptions<AutocompleteItem>> {
  emptyQuerySearchOptions: () => JSXElementConstructor<any>|ReactElement,
  initialQuery: string,
}

const Autocomplete = (
  props: Props,
) => {
  const {
    emptyQuerySearchOptions,
    initialQuery,
  } = props;

  const [autocompleteState, setAutocompleteState] = useState<
    AutocompleteState<AutocompleteItem>
  >({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: initialQuery,
    activeItemId: null,
    status: 'idle',
  });
  const [showPreview, setShowPreview] = useState(false);
  const [initialQueryPerformed, setInitialQueryPerformed] = useState(!!autocompleteState.query);
  const [autoCompletePanelStyle, setAutoCompletePanelStyle] = useState<{
    height?: string|number,
    maxHeight?: number|string,
    left?: string|number,
  }>({});
  const { width } = useWindowDimensions();

  useEffect(() => {
    // On mobile, treat the search dropdown like a modal, and don't allow the body to be
    // scrolled in the background.
    if (width <= 768) {
      if (autocompleteState.isOpen || showPreview) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }
    }
    setAutoCompletePanelStyle((currentAutocompletePanelStyle) => {
      const updatedStyle = { ...currentAutocompletePanelStyle };
      const nav = document.querySelector('.navbar.header-menu');
      const navHeight = nav
        ? nav.clientHeight + nav.getBoundingClientRect().top
        : undefined;
      const searchBox = document.querySelector('.input-group.search-input');
      const searchBoxBottom = searchBox
        ? searchBox.getBoundingClientRect().bottom
        : 0;
      const searchBoxLeft = searchBox
        ? searchBox.getBoundingClientRect().left
        : 0;
      // On mobile devices, set max height to fill the maximum available screen space.
      if (width <= 768) {
        if (navHeight) {
          updatedStyle.height = window.innerHeight - navHeight;
        } else if (searchBoxBottom) {
          updatedStyle.height = window.innerHeight - searchBoxBottom;
          updatedStyle.left = searchBoxLeft ? -searchBoxLeft : 0;
        } else {
          updatedStyle.height = '100vh';
        }
      } else {
        // On desktop widths, set height to max available screen height, or
        // 600px, whichever is smaller
        updatedStyle.maxHeight = navHeight ? Math.min(window.innerHeight - navHeight, 600) : 600;
      }
      return updatedStyle;
    });
  }, [
    autocompleteState.isOpen,
    showPreview,
  ]);

  const updateQuery = (event: SyntheticEvent, query: string) => {
    event.target.dispatchEvent(new CustomEvent('updateQuery', {
      bubbles: true,
      detail: {
        query: () => query,
      },
    }));
  };

  // This will trigger an event that can be used to update the query in other components
  const handleUpdateQuery = (event: any) => {
    updateQuery(event, event.target.value);
  };

  useEffect(() => {
    const globalSearchInput = document.querySelector('.js-search-suggestions-input');
    if (globalSearchInput) {
      globalSearchInput.addEventListener('input', handleUpdateQuery);
    }
    return () => {
      globalSearchInput?.removeEventListener('input', handleUpdateQuery);
    };
  }, []);

  const autocomplete = useMemo(() => createAutocomplete<
    AutocompleteItem,
    BaseSyntheticEvent,
    MouseEvent,
    KeyboardEvent
  >({
    initialState: autocompleteState,
    onStateChange({ state }) {
      setAutocompleteState(state);
      const { query } = state;
      if (query) {
        setInitialQueryPerformed(true);
      }
    },
    ...props,
  }), [props]);
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { getEnvironmentProps } = autocomplete;

  useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined;
    }

    const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    });

    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchmove', onTouchMove);

    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [getEnvironmentProps, autocompleteState.isOpen]);

  const closeAutocomplete = () => {
    setShowPreview(false);
    setAutocompleteState((a) => ({
      ...a,
      isOpen: false,
    }));
  };

  const resetAutocomplete = useCallback((event) => {
    formRef?.current?.reset();
    closeAutocomplete();
    // Dispatch event to reset query param
    updateQuery(event, '');
  }, [formRef, closeAutocomplete, updateQuery]);

  // Closes form when external click is registered
  useOutsideClickAlerter(formRef, closeAutocomplete);

  const rootProps = autocomplete.getRootProps({});
  const formProps = autocomplete.getFormProps({ inputElement: inputRef.current });
  const inputProps = autocomplete.getInputProps({ inputElement: inputRef.current });
  const panelProps = autocomplete.getPanelProps({});
  const listProps = autocomplete.getListProps();

  return (
    <div
      className={`aa-input-container ${autocompleteState.isOpen ? 'active' : ''}`}
      id="aa-input-container"
      {...rootProps}
    >
      <form
        ref={formRef}
        {...formProps}
        onSubmit={(event) => {
          closeAutocomplete();
          formProps.onSubmit(event);
        }}
      >
        <div className="input-group search-input">
          <div className="search-input">
            <div className="algolia-react-autocomplete">
              <div className="aa-input-container">
                <input
                  key="input"
                  className="aa-input-search js-search-suggestions-input"
                  name="search"
                  ref={inputRef}
                  {...inputProps}
                  onFocus={() => setShowPreview(true)}
                />
              </div>
              {autocompleteState.isOpen && (
                <button
                  id="autocomplete-clear"
                  className="clear-btn"
                  title="Clear"
                  type="reset"
                  onClick={(e) => {
                    e.preventDefault();
                    resetAutocomplete(e);
                  }}
                >
                  <i className="fa fa-times" />
                </button>
              )}
            </div>
          </div>
          <div className="input-group-append">
            <button type="submit" className="input-group-text" id="basic-addon1">
              <i className="fa fa-class fa-search" />
            </button>
          </div>
        </div>
        {autocompleteState.isOpen && (
          <div
            ref={panelRef}
            className={classNames(
              'aa-dropdown-menu',
              { 'aa-Panel--stalled': autocompleteState.status === 'stalled' },
            )}
            style={autoCompletePanelStyle}
            {...panelProps}
          >
            {autocompleteState.collections.map((collection) => {
              const { source, items } = collection;
              // TODO: Figure out why templates isn't typed correctly
              const { sourceId, templates }: any = source;
              return (
                <div
                  key={`${sourceId}-wrapper`}
                  className="aa-dropdown-menus"
                >
                  {templates?.header && templates.header()}
                  {items.length > 0 && (
                    <div
                      className="aa-suggestions"
                      {...listProps}
                    >
                      {items.map((item) => (
                        <a
                          key={item.objectID}
                          className={classNames(
                            'aa-suggestion',
                            { selected: autocompleteState.activeItemId === item.__autocomplete_id },
                          )}
                          href={item.route}
                          {...autocomplete.getItemProps({ item, source })}
                        >
                          {templates.item({ item })}
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Footer anchor tag will not be navigable with arrows using keyboard nav; */}
                  {/* if desired, custom navigation will need to be built */}
                  {templates?.footer && templates.footer()}
                </div>
              );
            })}
          </div>
        )}
        {showPreview && !autocompleteState.isOpen && !initialQueryPerformed && (
          <>{emptyQuerySearchOptions && emptyQuerySearchOptions()}</>
        )}
      </form>
    </div>
  );
};

export default Autocomplete;
